/**
 * Agent identity — the single source of truth that turns a config agent
 * (`GET /config` `agents[]`) into the visual identity used everywhere an agent
 * appears: the conversation-card badge, the launcher tile, the agent strip.
 *
 * Config-driven, never a hardcoded agent list. Each agent's `scope`, `accent`
 * (a design-token NAME) and `icon` (a glyph NAME) come from
 * the agent's manifest → the `AgentInfo` catalogue. This resolver maps:
 *   - accent token-name → Tailwind class fragments (badge / tint / text / ring)
 *   - icon name         → a lucide-react (or custom) component
 * Both maps fall back to a NEUTRAL identity for an unknown slug/accent/icon, so
 * a future agent (e.g. "AI Studio") shipped by the backend renders sanely with
 * no frontend release and never crashes.
 *
 * Display names are localised via `useAgentLabel()` (i18n keyed by slug),
 * falling back to config (`entry.name`) for an unknown slug — so a backend-only
 * agent still renders. (Identity = scope/accent/icon here; the name is resolved
 * by the catalogue hook — see the conversation card.)
 */
import type { ComponentType } from 'react'
import {
  ArrowRightLeft,
  Users,
  Sparkles,
  CalendarDays,
  GitBranch,
  BookOpen,
  LayoutGrid,
  ClipboardList,
  KeyRound,
  ShieldCheck,
  Bot,
} from 'lucide-react'
import { Venn } from '@/components/icons/Venn'

/** Which scope tab an agent's conversations live under on the home screen. */
export type AgentScope = 'patient' | 'personal' | 'team'

/** Anything renderable with a `className` — lucide icons and our custom Venn. */
export type AgentGlyph = ComponentType<{ className?: string }>

/**
 * Tailwind class fragments for one accent. Kept as literal strings (not built
 * by interpolation) so the Tailwind JIT scanner sees every class.
 *  - `badge`: solid colour square + white glyph (the agent badge / launcher tile)
 *  - `tint` : soft surface wash (selected rows, badge backgrounds)
 *  - `text` : accessible dark-on-white text/icon tone (≥ AA)
 *  - `ring` : focus / emphasis ring colour
 */
export interface AgentAccentClasses {
  badge: string
  tint: string
  text: string
  ring: string
}

/**
 * The minimal config shape this resolver reads. `AgentInfo` from the catalogue
 * is structurally assignable to this (slug + name required; scope/accent/icon
 * optional so it compiles regardless of backend/catalogue version). Kept local
 * and decoupled so the resolver doesn't depend on the full API type.
 */
export interface AgentCatalogueEntry {
  slug: string
  name: string
  scope?: AgentScope
  accent?: string
  icon?: string
}

/** The resolved identity consumed by every agent-facing component. */
export interface AgentIdentity {
  slug: string
  name: string
  scope: AgentScope
  accent: AgentAccentClasses
  Icon: AgentGlyph
}

/**
 * accent token-name → class fragments. Keys are the token names the backend
 * emits in `agent.accent`. Token-backed (no raw hex): every class resolves to a
 * CSS var via tailwind.config.ts. `violet`/`terracotta` are the agent-accent
 * ramps added in specs/design-tokens.yaml.
 */
const ACCENT_CLASSES: Record<string, AgentAccentClasses> = {
  primary: {
    badge: 'bg-primary text-white',
    tint: 'bg-primary-50',
    text: 'text-primary-700',
    ring: 'ring-primary',
  },
  'tone-ok': {
    badge: 'bg-tone-ok text-white',
    tint: 'bg-tone-ok-tint',
    text: 'text-tone-ok',
    ring: 'ring-tone-ok',
  },
  violet: {
    badge: 'bg-violet-500 text-white',
    tint: 'bg-violet-50',
    text: 'text-violet-700',
    ring: 'ring-violet-500',
  },
  citation: {
    badge: 'bg-citation-500 text-white',
    tint: 'bg-citation-50',
    text: 'text-citation-700',
    ring: 'ring-citation-500',
  },
  'neutral-700': {
    badge: 'bg-neutral-700 text-white',
    tint: 'bg-neutral-100',
    text: 'text-neutral-700',
    ring: 'ring-neutral-400',
  },
  terracotta: {
    badge: 'bg-terracotta-500 text-white',
    tint: 'bg-terracotta-50',
    text: 'text-terracotta-700',
    ring: 'ring-terracotta-500',
  },
}

/** Neutral fallback accent for an unknown/absent token name. */
const NEUTRAL_ACCENT: AgentAccentClasses = ACCENT_CLASSES['neutral-700']

/**
 * icon name → glyph component. Keys are the exact `agent.icon` strings from
 * the agent manifests: the lucide-react export names for the standard glyphs,
 * and `venn` (lowercase) for our custom compatibility glyph.
 */
const ICON_REGISTRY: Record<string, AgentGlyph> = {
  ArrowRightLeft,
  Users,
  Sparkles,
  CalendarDays,
  GitBranch,
  BookOpen,
  LayoutGrid,
  ClipboardList,
  KeyRound,
  ShieldCheck,
  venn: Venn,
}

/** Neutral fallback glyph for an unknown/absent icon name. */
const FALLBACK_ICON: AgentGlyph = Bot

/** Default scope when the catalogue entry omits one (or the slug is unknown). */
const DEFAULT_SCOPE: AgentScope = 'personal'

/**
 * Resolve a config agent slug to its full visual identity. Pure and total:
 * an unknown slug, accent, or icon yields a neutral-but-valid identity rather
 * than throwing — the agent grid must never crash on a newer backend.
 */
export function resolveAgentIdentity(
  slug: string,
  catalogue: readonly AgentCatalogueEntry[]
): AgentIdentity {
  const entry = catalogue.find((a) => a.slug === slug)
  return {
    slug,
    name: entry?.name ?? slug,
    scope: entry?.scope ?? DEFAULT_SCOPE,
    accent: (entry?.accent && ACCENT_CLASSES[entry.accent]) || NEUTRAL_ACCENT,
    Icon: (entry?.icon && ICON_REGISTRY[entry.icon]) || FALLBACK_ICON,
  }
}
